import {
  canonicalizeFeedbackCategoryIds,
  type FeedbackCategoryId,
} from "../feedback/feedbackTaxonomy";

/**
 * Staff scene flags (C2) as the CRM reads them: dashboard-api
 * `GET /video-sessions/{id}/scene-flags` returns every flag Echoflicks staff put
 * on a Studio film's scenes (stored by the bot on the Studio table), each with
 * the snapshot the bot froze from the film's manifest and its scene's
 * residual-motion QC verdict. This module maps that wire shape and ties the
 * portal's tag list to the CRM's own feedback taxonomy (plan G17): the two
 * label sets stay separate stores, but one vocabulary.
 */

/** The bot's closed tag list (studio-scene-flag.v1), in its canonical order. */
export const SCENE_FLAG_TAGS = [
  "poltergeist",
  "frozen_person",
  "talking_mouth",
  "bad_crop",
  "wrong_room",
  "floor_plan_shown",
  "other",
] as const;

export type SceneFlagTag = (typeof SCENE_FLAG_TAGS)[number];

export const SCENE_FLAG_TAG_LABELS: Readonly<Record<SceneFlagTag, string>> = {
  poltergeist: "Poltergeist",
  frozen_person: "Frozen person",
  talking_mouth: "Talking mouth",
  bad_crop: "Bad crop",
  wrong_room: "Wrong room",
  floor_plan_shown: "Floor plan shown",
  other: "Other",
};

/**
 * C2 tag -> the CRM "Feedback here" categories it means (feedbackTaxonomy.ts).
 * Objects that move by themselves and people who freeze or talk are animation
 * faults of the i2v clip (the two person tags are also about the subject); a
 * bad crop is framing; the wrong room and a floor plan on screen are the wrong
 * media chosen for the scene.
 */
export const SCENE_FLAG_TAG_FEEDBACK_CATEGORIES: Readonly<
  Record<SceneFlagTag, readonly FeedbackCategoryId[]>
> = {
  poltergeist: ["i2v_animation"],
  frozen_person: ["i2v_animation", "subject_expression"],
  talking_mouth: ["i2v_animation", "subject_expression"],
  bad_crop: ["framing_crop"],
  wrong_room: ["media_selection"],
  floor_plan_shown: ["media_selection"],
  other: ["other"],
};

/** The I2VFallbackChain section of a scene: the target of the card's "i2v attempts" link. */
export const i2vAttemptsAnchorId = (sceneId: string | number) =>
  `i2v-attempts-scene-${sceneId}`;

export const isSceneFlagTag = (value: unknown): value is SceneFlagTag =>
  typeof value === "string" &&
  (SCENE_FLAG_TAGS as readonly string[]).includes(value);

/** The CRM feedback categories of a set of C2 tags, in taxonomy order (unknown tags ignored). */
export function feedbackCategoriesForSceneFlagTags(
  tags: readonly unknown[],
): FeedbackCategoryId[] {
  return canonicalizeFeedbackCategoryIds(
    tags
      .filter(isSceneFlagTag)
      .flatMap((tag) => SCENE_FLAG_TAG_FEEDBACK_CATEGORIES[tag]),
  );
}

export type SceneFlagResidualMotion = {
  verdict: string | null;
  decision: string | null;
  effectiveMode: string | null;
  reasons: string[];
  attempt: number | null;
  disposition: string | null;
  matchedFlaggedClip: boolean;
  scores: Record<string, number>;
};

export type SceneFlagQcFinding = {
  code: string;
  severity: string | null;
  message: string | null;
};

export type SceneFlagSnapshot = {
  status: string | null;
  missing: string[];
  clipKey: string | null;
  clipSha256: string | null;
  provider: string | null;
  concreteProvider: string | null;
  model: string | null;
  inherited: boolean;
  sceneRevisionId: string | null;
  packetId: string | null;
  positivePromptSha256: string | null;
  promptExcerpt: string | null;
  promptChars: number | null;
  configReleaseDigest: string | null;
  sourceMediaSha256: string | null;
  reMotion: Record<string, unknown> | null;
};

export type EchoflicksSessionSceneFlag = {
  id: string;
  flag_id: string;
  session_id: string | null;
  echo_id: string | null;
  scene_id: number | null;
  scene_index: number | null;
  tags: SceneFlagTag[];
  feedback_categories: FeedbackCategoryId[];
  note: string;
  status: "active" | "withdrawn";
  created_at: string | null;
  withdrawn_at: string | null;
  reporter_email: string | null;
  reporter_portal_user_id: string | null;
  reporter_sales_id: number | null;
  snapshot: SceneFlagSnapshot;
  qc_report_status: string | null;
  residual_motion: SceneFlagResidualMotion | null;
  residual_motion_attempts: Array<{
    attempt: number | null;
    disposition: string | null;
    verdict: string | null;
    decision: string | null;
  }>;
  qc_findings: SceneFlagQcFinding[];
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);
const text = (value: unknown): string | null =>
  typeof value === "string" && value ? value : null;
const count = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;
const texts = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];

function mapSnapshot(raw: unknown): SceneFlagSnapshot {
  const snapshot = isRecord(raw) ? raw : {};
  const clip = isRecord(snapshot.clip) ? snapshot.clip : {};
  const i2v = isRecord(snapshot.i2v) ? snapshot.i2v : {};
  const prompt = isRecord(snapshot.prompt) ? snapshot.prompt : {};
  const recipe = isRecord(snapshot.render_recipe) ? snapshot.render_recipe : {};
  return {
    status: text(snapshot.snapshot_status),
    missing: texts(snapshot.missing),
    clipKey: text(clip.s3_key),
    clipSha256: text(clip.output_media_sha256),
    provider: text(i2v.provider),
    concreteProvider: text(i2v.concrete_provider),
    model: text(i2v.model),
    inherited: i2v.source === "inherited",
    sceneRevisionId: text(snapshot.scene_revision_id),
    packetId: text(snapshot.packet_id),
    positivePromptSha256: text(prompt.positive_sha256),
    promptExcerpt: text(prompt.final_positive_excerpt),
    promptChars: count(prompt.final_positive_chars),
    configReleaseDigest: text(recipe.config_release_digest),
    sourceMediaSha256: text(recipe.source_media_sha256),
    reMotion: isRecord(snapshot.re_motion) ? snapshot.re_motion : null,
  };
}

function mapResidualMotion(raw: unknown): SceneFlagResidualMotion | null {
  if (!isRecord(raw)) return null;
  const scores = isRecord(raw.scores) ? raw.scores : {};
  return {
    verdict: text(raw.verdict),
    decision: text(raw.decision),
    effectiveMode: text(raw.effective_mode),
    reasons: texts(raw.reasons),
    attempt: count(raw.attempt),
    disposition: text(raw.disposition),
    matchedFlaggedClip: raw.matched_flagged_clip === true,
    scores: Object.fromEntries(
      Object.entries(scores).filter(
        (entry): entry is [string, number] =>
          typeof entry[1] === "number" && Number.isFinite(entry[1]),
      ),
    ),
  };
}

/** One row of `GET /video-sessions/{id}/scene-flags` -> the CRM record. */
export function mapSceneFlag(
  record: Record<string, unknown>,
): EchoflicksSessionSceneFlag {
  const flagId = String(record.flag_id ?? record.id ?? "");
  const reporter = isRecord(record.reporter) ? record.reporter : {};
  const qc = isRecord(record.qc) ? record.qc : {};
  const tags = texts(record.tags).filter(isSceneFlagTag);
  return {
    id: flagId,
    flag_id: flagId,
    session_id: text(record.session_id),
    echo_id: text(record.echo_id),
    scene_id: count(record.scene_id),
    scene_index: count(record.scene_index),
    tags,
    feedback_categories: feedbackCategoriesForSceneFlagTags(tags),
    note: typeof record.note === "string" ? record.note : "",
    status: record.status === "withdrawn" ? "withdrawn" : "active",
    created_at: text(record.created_at),
    withdrawn_at: text(record.withdrawn_at),
    reporter_email: text(reporter.email),
    reporter_portal_user_id: text(reporter.portal_user_id),
    reporter_sales_id: count(reporter.shadow_of_sales_id),
    snapshot: mapSnapshot(record.snapshot),
    qc_report_status: text(qc.report_status),
    residual_motion: mapResidualMotion(qc.residual_motion),
    residual_motion_attempts: (Array.isArray(qc.residual_motion_attempts)
      ? qc.residual_motion_attempts
      : []
    )
      .filter(isRecord)
      .map((attempt) => ({
        attempt: count(attempt.attempt),
        disposition: text(attempt.disposition),
        verdict: text(attempt.verdict),
        decision: text(attempt.decision),
      })),
    qc_findings: (Array.isArray(qc.findings) ? qc.findings : [])
      .filter(isRecord)
      .map((finding) => ({
        code: String(finding.code ?? ""),
        severity: text(finding.severity),
        message: text(finding.message),
      })),
  };
}

export type SceneFlagGroup = {
  sceneId: number | null;
  sceneIndex: number | null;
  flags: EchoflicksSessionSceneFlag[];
  activeCount: number;
  /** The newest flag's view of the scene: its snapshot and QC join. */
  latest: EchoflicksSessionSceneFlag;
};

/** Flags grouped per scene (scene order, then flag time), active first within a scene. */
export function groupSceneFlags(
  flags: readonly EchoflicksSessionSceneFlag[],
): SceneFlagGroup[] {
  const groups = new Map<string, EchoflicksSessionSceneFlag[]>();
  for (const flag of flags) {
    const key = String(flag.scene_id ?? "unknown");
    groups.set(key, [...(groups.get(key) ?? []), flag]);
  }
  return [...groups.values()]
    .map((sceneFlags) => {
      const ordered = [...sceneFlags].sort(
        (left, right) =>
          Number(left.status === "withdrawn") -
            Number(right.status === "withdrawn") ||
          String(right.created_at ?? "").localeCompare(
            String(left.created_at ?? ""),
          ),
      );
      const newest = [...sceneFlags].sort((left, right) =>
        String(right.created_at ?? "").localeCompare(
          String(left.created_at ?? ""),
        ),
      )[0];
      return {
        sceneId: newest.scene_id,
        sceneIndex: newest.scene_index,
        flags: ordered,
        activeCount: sceneFlags.filter((flag) => flag.status === "active")
          .length,
        latest: newest,
      };
    })
    .sort(
      (left, right) =>
        (left.sceneIndex ?? left.sceneId ?? Number.MAX_SAFE_INTEGER) -
        (right.sceneIndex ?? right.sceneId ?? Number.MAX_SAFE_INTEGER),
    );
}

/**
 * The residual-motion verdict of the flagged clip, as one badge. Absent means
 * the check did not run for that clip (no motion budget), which is itself worth
 * seeing beside a "poltergeist" flag.
 */
export function residualMotionLabel(
  motion: SceneFlagResidualMotion | null,
  reportStatus: string | null,
): { text: string; variant: "destructive" | "secondary" | "outline" } {
  if (!motion) {
    if (reportStatus === "loaded") {
      return { text: "Residual motion: not checked", variant: "outline" };
    }
    if (reportStatus === "not_registered") {
      return { text: "No QC report yet", variant: "outline" };
    }
    return { text: "QC report unavailable", variant: "outline" };
  }
  const verdict = motion.verdict ?? "unknown";
  const decision =
    motion.decision && motion.decision !== "accept"
      ? ` → ${motion.decision}`
      : "";
  return {
    text: `Residual motion: ${verdict}${decision}`,
    variant:
      verdict === "fail"
        ? "destructive"
        : verdict === "flag"
          ? "secondary"
          : "outline",
  };
}
